import React, { useCallback, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import CrudForm, { FormConfiguration } from "../shared/crud/form";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import actions from "../../lib/actions";
import { useProperty } from "../../components/property-provider";
import { messages } from "./analyticsConfiguration.messages";
import { buildFormConfiguration, buildSchema } from "./analyticsConfiguration.formConfig";
import { AnalyticsConfiguration } from "./AnalyticsConfiguration";
import { getPropertyConfiguration, storePropertyConfiguration } from "../shared/api/middlewareApi";
import { useHttpClient } from "../../lib/http";
import { IConfig, useConfig } from "../../lib/config";
import styled from "styled-components";
import { regenPropertyCache } from "../common/api";

const StylesContainer = styled("div")`
  input:not([type="radio"]) {
    width: 300px;
  }
`;

function FormContainer({ children }: { children?: React.ReactNode }) {
  return (
    <StylesContainer>
      <div className="alert alert-warning" role="alert">
        <FormattedMessage {...messages.analyticsConfigurationWarning} />
      </div>
      {children}
    </StylesContainer>
  );
}
function AnalyticsConfigurationForm() {
  const configuration: IConfig = useConfig();
  const intl = useIntl();
  const propertyInfo = useProperty();
  const middlewareClient = useHttpClient(configuration.middlewareEndpoint);
  const apiClient = useHttpClient(configuration.secureApiEndpoint);

  const handleGetOne = useCallback(async () => {
    const data = await getPropertyConfiguration(middlewareClient, propertyInfo.ticker, [
      "gtmCodes",
    ]);
    const codes = data?.gtmCodes?.length
      ? data?.gtmCodes
          .split(",")
          .map((it: string) => it.trim())
          .join(",")
      : undefined;
    return { id: "analytics", gtmCodes: codes };
  }, [middlewareClient, propertyInfo.ticker]);

  const handleUpdate = useCallback(
    async (entity: AnalyticsConfiguration) => {
      await storePropertyConfiguration(middlewareClient, propertyInfo.ticker, {
        gtmCodes: entity.gtmCodes ?? "",
      });
      await regenPropertyCache(apiClient, propertyInfo.ticker);
      alert(intl.formatMessage(messages.saveSuccess));
    },
    [apiClient, intl, middlewareClient, propertyInfo.ticker]
  );

  const { formConfiguration, schema } = useMemo<{
    formConfiguration: FormConfiguration<AnalyticsConfiguration>;
    schema: any;
  }>(
    () => ({
      schema: buildSchema(),
      formConfiguration: buildFormConfiguration(),
    }),
    []
  );

  return (
    <CrudForm<AnalyticsConfiguration>
      id={"analytics"}
      schema={schema}
      formConfiguration={formConfiguration}
      container={FormContainer}
      messages={messages}
      onGet={handleGetOne}
      onUpdate={handleUpdate}
      canUpdate={actions.ModifyGoogleAnalyticsSettings}
      enumsOptional
    />
  );
}
export default withAuthorizationVerifier(AnalyticsConfigurationForm);
